import React from 'react';
import {
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { formatRelativeTime } from './utils';

const MarkingTab = ({
  form,
  setForm,
  settings,
  saving,
  onSave,
  onReset,
}) => {
  const updateField = (field) => (event) => {
    const value = field === 'azureUseActiveDirectory' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateParameter = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      parameters: { ...prev.parameters, [field]: event.target.value },
    }));
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Marking defaults for backend scoring</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField select label="Provider" value={form.provider} onChange={updateField('provider')} sx={{ minWidth: 200 }}>
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="azure">Azure OpenAI</MenuItem>
          </TextField>
        </Stack>

        {form.provider === 'openai' && (
          <TextField
            label="OpenAI API key"
            type="password"
            value={form.parentApiKey}
            onChange={updateField('parentApiKey')}
            helperText={settings?.openai?.marking?.openaiKeyConfigured
              ? 'Leave blank to keep the existing key'
              : 'Provide the API key used to call the Responses API'}
          />
        )}

        {form.provider === 'azure' && (
          <Stack spacing={2}>
            <TextField
              label="Azure endpoint"
              value={form.azureEndpoint}
              onChange={updateField('azureEndpoint')}
              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com) or full responses endpoint (https://your-resource.openai.azure.com/openai/responses?api-version=...)"
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Deployment id"
                value={form.azureDeploymentId}
                onChange={updateField('azureDeploymentId')}
                helperText="Azure deployment id for your marking model (optional if using full responses endpoint)"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="API version"
                value={form.azureApiVersion}
                onChange={updateField('azureApiVersion')}
                helperText="Azure API version (e.g., 2025-04-01-preview). Optional if included in endpoint."
                sx={{ minWidth: 200 }}
              />
            </Stack>
            <TextField
              label="Resource name (optional)"
              value={form.azureResourceName}
              onChange={updateField('azureResourceName')}
              helperText="Resource name used for logging or diagnostics"
            />
            <TextField
              label="Azure OpenAI API key"
              type="password"
              value={form.azureApiKey}
              onChange={updateField('azureApiKey')}
              helperText={settings?.openai?.marking?.azureKeyConfigured
                ? 'Leave blank to keep the existing key'
                : 'Provide the Azure OpenAI key for this deployment'}
            />
            <FormControlLabel
              control={<Switch checked={Boolean(form.azureUseActiveDirectory)} onChange={updateField('azureUseActiveDirectory')} />}
              label="Use Azure Active Directory (access token authentication)"
            />
          </Stack>
        )}

        <TextField
          label="Marking model"
          value={form.markingModel}
          onChange={updateField('markingModel')}
          sx={{ minWidth: 240 }}
          helperText={settings?.openai?.allowedMarkingModels?.length
            ? `Enter the exact responses model id (e.g., ${settings.openai.allowedMarkingModels.slice(0, 3).join(', ')})`
            : 'Enter the exact Responses/GPT model to use for scoring.'}
          autoComplete="off"
          inputProps={{ spellCheck: 'false' }}
        />

        <Typography variant="subtitle1">Marking parameters (GPT-5 ready)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField label="Temperature" type="number" value={form.parameters.temperature} onChange={updateParameter('temperature')} helperText="Default 0 for deterministic scoring" fullWidth inputProps={{ step: 0.1, min: 0, max: 2 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Max output tokens" type="number" value={form.parameters.maxOutputTokens} onChange={updateParameter('maxOutputTokens')} helperText="Leave blank to let the model decide" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Top P" type="number" value={form.parameters.topP} onChange={updateParameter('topP')} helperText="Optional nucleus sampling (0-1)" fullWidth inputProps={{ step: 0.05, min: 0, max: 1 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Frequency penalty" type="number" value={form.parameters.frequencyPenalty} onChange={updateParameter('frequencyPenalty')} helperText="Discourage repetition (-2 to 2)" fullWidth inputProps={{ step: 0.1, min: -2, max: 2 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Presence penalty" type="number" value={form.parameters.presencePenalty} onChange={updateParameter('presencePenalty')} helperText="Encourage diverse points (-2 to 2)" fullWidth inputProps={{ step: 0.1, min: -2, max: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Reasoning effort" value={form.parameters.reasoningEffort} onChange={updateParameter('reasoningEffort')} helperText="Optional reasoning mode hint (e.g., medium, high)" fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField select label="Verbosity" value={form.parameters.verbosity} onChange={updateParameter('verbosity')} helperText="Controls comment length (low = concise)" fullWidth>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {settings?.openai?.marking?.lastUpdatedAt && (
          <Typography variant="body2" color="text.secondary">
            Last updated {formatRelativeTime(settings.openai.marking.lastUpdatedAt)}
          </Typography>
        )}

        <Stack direction="row" spacing={2}>
          <Button variant="contained" color="primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save marking defaults'}
          </Button>
          <Button variant="outlined" onClick={onReset}>Reset</Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default MarkingTab;
